import React, { useMemo, useState } from 'react'
import classNames from 'classnames'
import { CountryCode, E164Number } from 'libphonenumber-js'
import { isValidPhoneNumber } from 'libphonenumber-js/max'
import { isEmpty } from 'lodash'
import isNil from 'lodash/isNil'
import flags from 'react-phone-number-input/flags'
import en from 'react-phone-number-input/locale/en.json'
import es from 'react-phone-number-input/locale/es.json'
import pt from 'react-phone-number-input/locale/pt-BR.json'
import PhoneInput from 'react-phone-number-input/max'
import { DEFAULT_REGION } from '@/constants/regions'
import { useTranslate } from '@/utils/translate/translate-client'

export type InternationalPhoneNumberInputProps = {
  onPhoneValue: (v: E164Number | undefined) => void
  onValidPhone: (v: boolean) => void
  countryCode: CountryCode
  inputClassName?: string
  placeholder?: string
  wrapperClassName?: string
  autoFocus?: boolean
}

const labelMap: Record<string, unknown> = {
  // North America
  us: en,
  ca: en,

  // Central America
  bz: es,
  cr: es,
  gt: es,
  hn: es,
  mx: es,
  ni: es,
  pa: es,
  sv: es,

  // South America
  ar: es,
  bo: es,
  cl: es,
  co: es,
  ec: es,
  gy: es,
  py: es,
  pe: es,
  sr: es,
  uy: es,
  ve: es,
  br: pt,
}

export const InternationalPhoneNumberInput = ({
  onPhoneValue,
  onValidPhone,
  countryCode = DEFAULT_REGION,
  inputClassName = 'bg-core-gray-100 p-3.5 border border-r-0 border-y-0 border-l-core-gray-200',
  placeholder,
  wrapperClassName = 'rounded-lg border border-core-gray-200 bg-core-gray-100',
  autoFocus = false,
}: InternationalPhoneNumberInputProps) => {
  const countryLabels = isNil(labelMap[countryCode.toLowerCase()]) ? en : labelMap[countryCode.toLowerCase()]
  const { t } = useTranslate('common')
  const [value, setValue] = useState<E164Number | undefined>()
  const sanitizedCountryCode = isEmpty(countryCode) ? 'US' : countryCode
  const inputComponent = useMemo(
    () =>
      React.forwardRef<HTMLInputElement>(function InputComponent(props, ref) {
        return (
          <input
            {...props}
            autoFocus={autoFocus}
            className={classNames('w-full h-full', inputClassName)}
            ref={ref}
            type="tel"
          />
        )
      }),
    [inputClassName, autoFocus],
  )

  return (
    <PhoneInput
      defaultCountry={sanitizedCountryCode}
      country={sanitizedCountryCode}
      labels={countryLabels as never}
      flags={flags}
      className={wrapperClassName}
      placeholder={placeholder || t('entryPhoneNumber', 'Enter phone number')}
      value={value}
      inputComponent={inputComponent}
      onChange={(v) => {
        setValue(v)
        onPhoneValue(v)
        v ? onValidPhone(isValidPhoneNumber(v)) : onValidPhone(false)
      }}
    />
  )
}
